import { FC, useMemo } from "react";
import { useSelector } from "react-redux";

import { Avatar, DesignToken, RadiusToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { selectTeams } from "store/entities/teams";
import { withVar } from "themes/helpers";

interface IProps {
  teamIds: string[];
  avatarsToDisplay?: number;
  className?: string;
}

const TeamAvatar: FC<{ teamName: string; zIndex: number }> = ({ teamName, zIndex }) => (
  <Block
    padding={withVar(SpacingToken.Spacing05)}
    borderRadius={withVar(RadiusToken.Radius2)}
    bg={withVar(DesignToken.SurfacePrimaryDefault)}
    marginLeft="-8px"
    css={{ zIndex }}
    title={teamName}
  >
    <Avatar text={teamName} type="text" size="xxsmall" shape="rounded-square" />
  </Block>
);

const TeamAvatarStack: FC<IProps> = ({ teamIds, avatarsToDisplay = 3, className }) => {
  const allTeams = useSelector(selectTeams);
  const teamsMap = useMemo(() => Object.values(allTeams).filter(({ id }) => teamIds.includes(id)), [allTeams, teamIds]);

  if (!teamIds?.length) {
    return null;
  }

  return (
    <Flex alignItems="center" justifyContent="center" gap={withVar(SpacingToken.Spacing1)} className={className}>
      {teamsMap.slice(0, avatarsToDisplay).map(({ id, name }, index) => (
        <TeamAvatar key={id} teamName={name} zIndex={avatarsToDisplay - index} />
      ))}
      {teamIds.length > avatarsToDisplay && (
        <Block
          title={teamsMap
            .slice(avatarsToDisplay)
            .map(({ name }) => name)
            .join("\n")}
        >
          <Text size="xs">+{teamIds.length - avatarsToDisplay}</Text>
        </Block>
      )}
    </Flex>
  );
};

export default TeamAvatarStack;
